import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, LogIn } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { formatError } from "@/lib/api";

export default function AdminLogin() {
    const { user, login, loading } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    if (loading)
        return (
            <div className="min-h-screen grid place-items-center bg-white">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
        );

    if (user) return <Navigate to="/admin/dashboard" replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            await login(email, password);
            toast.success("Welcome back");
            navigate("/admin/dashboard");
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-white" data-testid="admin-login-page">
            {/* Left: image panel */}
            <aside className="relative hidden lg:block bg-slate-950">
                <img
                    src="https://images.unsplash.com/photo-1563253746-350a0a877afa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwd29ya3NwYWNlJTIwdGVjaHxlbnwwfHx8fDE3ODEyMTQyNzB8MA&ixlib=rb-4.1.0&q=85"
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-60"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-slate-950 via-slate-950/70 to-transparent" />
                <div className="relative h-full flex flex-col justify-between p-12 text-white">
                    <div className="flex items-center gap-2">
                        <div className="w-9 h-9 bg-white text-slate-950 grid place-items-center rounded-md font-display font-black text-lg">
                            Ω
                        </div>
                        <div className="leading-tight">
                            <div className="font-display font-bold text-sm">Omega IT Ltd</div>
                            <div className="text-[11px] tracking-[0.18em] uppercase text-white/60">
                                Content Control Room
                            </div>
                        </div>
                    </div>
                    <div>
                        <div className="label-eyebrow text-white/60">Phase 1</div>
                        <h2 className="font-display text-3xl lg:text-4xl font-bold mt-3 leading-tight max-w-md">
                            Operate every page of Omega IT from one quiet desk.
                        </h2>
                        <p className="text-sm text-white/60 mt-4 max-w-md leading-relaxed">
                            Manage services, case studies, blog posts, pricing and
                            enquiries — without touching a line of code.
                        </p>
                    </div>
                </div>
            </aside>

            {/* Right: form */}
            <section className="flex items-center justify-center px-6 py-12 lg:px-16">
                <form
                    onSubmit={onSubmit}
                    className="w-full max-w-md space-y-7"
                    data-testid="admin-login-form"
                >
                    <div>
                        <div className="label-eyebrow">Admin Access</div>
                        <h1 className="font-display text-3xl lg:text-4xl font-black tracking-tight text-slate-950 mt-2">
                            Sign in to your CMS
                        </h1>
                        <p className="text-sm text-slate-500 mt-2">
                            Use the credentials your developer shared.
                        </p>
                    </div>

                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="admin@omegait.co.uk"
                                data-testid="login-email-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                autoComplete="current-password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="login-password-input"
                            />
                        </div>
                    </div>

                    <Button
                        type="submit"
                        size="lg"
                        disabled={submitting}
                        className="w-full bg-slate-950 hover:bg-slate-800 text-white font-semibold"
                        data-testid="login-submit-button"
                    >
                        {submitting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <>
                                <LogIn className="w-4 h-4 mr-2" />
                                Sign in
                            </>
                        )}
                    </Button>

                    <p className="text-xs text-slate-400 text-center">
                        Forgot the password? Ask your developer to reset it from the
                        server.
                    </p>
                </form>
            </section>
        </div>
    );
}
